/**
 * C-GTCC-COCKPIT-INFRA1 — Monitoring Cockpit tab "Infrastructure & VPS Health",
 * 1:1 after `GTCC_REFERENCE_2026-09-01.html`, `section[data-sec="infra"]`
 * (lines 336-384) plus the host table of the Executive Summary (lines
 * 228-238, rendered by `../components/HostTable.tsx`).
 *
 * Layout of the reference, element for element:
 *   - a 12 px sub-tab bar Overview · Hosts · Storage · Network · Backups ·
 *     Härtung,
 *   - the host chip row, the selected chip in accent,
 *   - Overview: "Host-Detail: <host>" with its freshness badge and the
 *     SIGNAL / WERT / BEWERTUNG table, "Storage-Inventar (Auszug
 *     Storage-Sub-Tab)", "Flotten-Kurzstatus", "TLS-Zertifikate",
 *   - Hosts: the large host table,
 *   - Storage: the full inventory the Overview quotes an excerpt of.
 *
 * Network, Backups and Härtung are sub-tabs the reference opens but does not
 * fill, and the G-GTCC-API-FLEET1 contract carries no endpoint for them: they
 * render the panel frame with "nicht messbar" rather than a number nobody
 * measured (Owner decisions P8/P9/P10).
 *
 * Every panel carries the age of its own source. Freshness comes from the
 * payload; a stale source keeps its warn colour and is never shown as healthy.
 */
import {
  Card,
  EmptyState,
  ErrorState,
  Inline,
  Stack,
  Tabs,
  type TabItem,
} from "@genius-tracker/design-system";
import { useLocale } from "@genius-tracker/i18n/react";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useCallback, useState, type CSSProperties, type ReactNode } from "react";

import {
  FLEET_HOSTS_KEY,
  FLEET_STORAGE_KEY,
  FLEET_SUMMARY_KEY,
  FLEET_TLS_KEY,
  fetchFleetHostDetail,
  fetchFleetHosts,
  fetchFleetStorage,
  fetchFleetSummary,
  fetchFleetTls,
  type FleetHostDetailDto,
  type FleetHostsDto,
  type FleetResult,
  type FleetStorageDto,
  type FleetStorageRowDto,
  type FleetSummaryDto,
  type FleetTlsDto,
} from "../../../api/fleet.js";
import {
  PANEL_HEADING_STYLE,
  formatGib,
  formatInteger,
  formatWholePercent,
  toneOfStatus,
  toneOfVerdict,
} from "../components/FleetPrimitives.js";
import { HostTable } from "../components/HostTable.js";
import { TABLE_STYLE, TD_STYLE, TH_STYLE } from "../../control/ControlPrimitives.js";
import { Dot, FreshBadge, Row, type CockpitTone } from "../../../ui/primitives.js";

/* ------------------------------------------------------- call-site pieces */

/** Grey "nicht messbar" — the only thing that ever stands in for a number. */
function NotMeasurable(props: { testId?: string }): ReactNode {
  const { t } = useLocale();
  return (
    <span
      data-testid={props.testId}
      style={{
        color: "var(--gt-sys-text-muted)",
        fontFamily: "var(--gt-sys-font-family-mono)",
        fontSize: "var(--gt-sys-font-size-xs)",
      }}
    >
      {t("admin.gtcc.cockpit.infra.not_measurable")}
    </span>
  );
}

/** A muted note under the rows of a panel — the reference sets it in mono. */
function FleetNote(props: { children: ReactNode; testId?: string }): ReactNode {
  return (
    <p
      data-testid={props.testId}
      style={{
        margin: 0,
        fontFamily: "var(--gt-sys-font-family-mono)",
        fontSize: "var(--gt-sys-font-size-xs)",
        color: "var(--gt-sys-text-muted)",
      }}
    >
      {props.children}
    </p>
  );
}

/**
 * `FreshBadge` (`ui/primitives.tsx`) carries no `testId`/`data-stale` of its
 * own; the wrapper below is the call-site adaptation that keeps both
 * (`InfraTab.test.tsx` reads the badge's text and, on a failed source, checks
 * the badge is entirely absent) without bending the shared primitive. Same
 * recipe as `LaunchView.tsx`'s `LaunchDot` and `AlinView.tsx`'s
 * `AlinFreshBadge`.
 */
function InfraFreshBadge(props: {
  seconds: number | undefined;
  measuredAt: string;
  stale?: boolean;
  testId: string;
}): ReactNode {
  const { t, locale } = useLocale();
  const stale = props.stale === true;
  if (props.seconds === undefined && Number.isNaN(new Date(props.measuredAt).getTime())) {
    return null;
  }
  const label =
    props.seconds === undefined
      ? t("admin.gtcc.cockpit.infra.fresh.measured_at", {
          time: new Intl.DateTimeFormat(locale, { timeStyle: "medium" }).format(
            new Date(props.measuredAt),
          ),
        })
      : props.seconds >= 60
        ? t("admin.gtcc.cockpit.infra.fresh.minutes", {
            minutes: new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(
              props.seconds / 60,
            ),
          })
        : t("admin.gtcc.cockpit.infra.fresh.seconds", {
            seconds: formatInteger(props.seconds, locale),
          });
  return (
    <span data-testid={props.testId} data-stale={stale ? "true" : "false"}>
      <FreshBadge label={label} stale={stale} />
    </span>
  );
}

/**
 * `Row` (`ui/primitives.tsx`) carries no `testId` of its own; the wrapper
 * below is the call-site adaptation that keeps it, same recipe as
 * `InfraFreshBadge` above.
 */
function InfraRow(props: {
  testId: string;
  tone: CockpitTone;
  label: ReactNode;
  value: ReactNode;
}): ReactNode {
  return (
    <div data-testid={props.testId}>
      <Row tone={props.tone} value={props.value}>
        {props.label}
      </Row>
    </div>
  );
}

/* --------------------------------------------------------------- sub-tabs */

export const INFRA_SUBTAB_IDS = [
  "overview",
  "hosts",
  "storage",
  "network",
  "backups",
  "hardening",
] as const;
export type InfraSubTabId = (typeof INFRA_SUBTAB_IDS)[number];

const DEFAULT_INFRA_SUBTAB: InfraSubTabId = "overview";

function isInfraSubTabId(value: string): value is InfraSubTabId {
  return (INFRA_SUBTAB_IDS as ReadonlyArray<string>).includes(value);
}

/** URL state: `?infra=<sub-tab>&host=<host>`, same shape as `?tab=`. */
const SUBTAB_PARAM = "infra";
const HOST_PARAM = "host";

function readQueryParam(param: string): string | null {
  if (typeof window === "undefined") return null;
  return new URLSearchParams(window.location.search).get(param);
}

function writeQueryParam(param: string, next: string): void {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  url.searchParams.set(param, next);
  window.history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`);
}

function initialSubTab(): InfraSubTabId {
  const requested = readQueryParam(SUBTAB_PARAM);
  if (requested !== null && isInfraSubTabId(requested)) return requested;
  return DEFAULT_INFRA_SUBTAB;
}

/* ---------------------------------------------------------------- queries */

/** Ticket floor of the cockpit: never refetch faster than 30 s. */
const FLEET_STALE_MS = 30_000;
const FLEET_REFETCH_MS = 60_000;

const FLEET_QUERY_OPTIONS = {
  staleTime: FLEET_STALE_MS,
  refetchInterval: FLEET_REFETCH_MS,
  refetchOnWindowFocus: false,
} as const;

/* ----------------------------------------------------------------- styles */

const GRID_STYLE: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "minmax(0, 2fr) minmax(0, 1fr)",
  gap: "var(--gt-sys-space-3)",
  alignItems: "start",
};

const CHIP_STYLE: CSSProperties = {
  /* Long-hand, not the `border` shorthand: the active chip only swaps the
     colour, and React warns when both forms describe the same value. */
  borderStyle: "solid",
  borderWidth: "1px",
  borderColor: "var(--gt-sys-border)",
  borderRadius: "var(--gt-sys-radius-md)",
  background: "var(--gt-sys-surface)",
  color: "var(--gt-sys-text-muted)",
  fontFamily: "var(--gt-sys-font-family-mono)",
  fontSize: "var(--gt-sys-font-size-xs)",
  paddingBlock: "var(--gt-sys-space-1)",
  paddingInline: "var(--gt-sys-space-2)",
  minBlockSize: "var(--gt-sys-touch-target-min)",
  cursor: "pointer",
};

const CHIP_ACTIVE_STYLE: CSSProperties = {
  ...CHIP_STYLE,
  color: "var(--gt-sys-accent)",
  borderColor: "var(--gt-sys-accent)",
};

const SUBTAB_BAR_STYLE: CSSProperties = { fontSize: "var(--gt-sys-font-size-xs)" };

/* ------------------------------------------------------------------ panel */

function Panel(props: {
  title: string;
  fresh?: ReactNode;
  testId: string;
  children: ReactNode;
}): ReactNode {
  return (
    <Card elevation={0}>
      <Stack gap={2}>
        <h3 style={PANEL_HEADING_STYLE} data-testid={`${props.testId}-title`}>
          {props.title}
          {props.fresh}
        </h3>
        <div data-testid={props.testId}>{props.children}</div>
      </Stack>
    </Card>
  );
}

/**
 * One gate for all five endpoints: a failed or pending read never falls back
 * to stale numbers, it says what happened.
 */
function FleetGate<T>(props: {
  query: UseQueryResult<FleetResult<T>>;
  testId: string;
  render: (data: T) => ReactNode;
}): ReactNode {
  const { t } = useLocale();
  const { query } = props;

  if (query.isPending || query.data === undefined) {
    return (
      <div data-testid={`${props.testId}-loading`}>
        <EmptyState title={t("admin.gtcc.cockpit.infra.loading")} />
      </div>
    );
  }
  if (query.data.ok) return props.render(query.data.data);

  const failure = query.data;
  const body =
    failure.kind === "disabled"
      ? t("admin.gtcc.cockpit.infra.failure.disabled")
      : failure.kind === "forbidden"
        ? t("admin.gtcc.cockpit.infra.failure.forbidden")
        : failure.kind === "upstream"
          ? t("admin.gtcc.cockpit.infra.failure.upstream", { status: failure.httpStatus })
          : t("admin.gtcc.cockpit.infra.failure.transport");
  return (
    <div data-testid={`${props.testId}-failure`}>
      <ErrorState severity="warn" title={t("admin.gtcc.cockpit.infra.failure.title")} body={body} />
    </div>
  );
}

/* ----------------------------------------------------------- host detail */

function HostDetailPanel(props: {
  host: string;
  query: UseQueryResult<FleetResult<FleetHostDetailDto>>;
}): ReactNode {
  const { t } = useLocale();
  const detail =
    props.query.data !== undefined && props.query.data.ok ? props.query.data.data : null;

  return (
    <Panel
      testId="gtcc-cockpit-infra-host-detail"
      title={t("admin.gtcc.cockpit.infra.detail.title", { host: props.host })}
      fresh={
        detail === null ? null : (
          <InfraFreshBadge
            seconds={detail.freshnessSeconds}
            measuredAt={detail.measuredAt}
            stale={detail.status === "stale"}
            testId="gtcc-cockpit-infra-host-detail-fresh"
          />
        )
      }
    >
      <FleetGate
        query={props.query}
        testId="gtcc-cockpit-infra-host-detail"
        render={(data) => (
          <table style={TABLE_STYLE} data-testid="gtcc-cockpit-infra-detail-table">
            <caption style={{ textAlign: "start", color: "var(--gt-sys-text-muted)" }}>
              {t("admin.gtcc.cockpit.infra.detail.caption", { host: props.host })}
            </caption>
            <thead>
              <tr>
                <th scope="col" style={DETAIL_HEAD_STYLE}>
                  {t("admin.gtcc.cockpit.infra.detail.col.signal")}
                </th>
                <th scope="col" style={DETAIL_HEAD_STYLE}>
                  {t("admin.gtcc.cockpit.infra.detail.col.value")}
                </th>
                <th scope="col" style={DETAIL_HEAD_STYLE}>
                  {t("admin.gtcc.cockpit.infra.detail.col.verdict")}
                </th>
              </tr>
            </thead>
            <tbody>
              {data.signals.map((signal) => (
                <tr key={signal.key} data-testid={`gtcc-cockpit-infra-signal-${signal.key}`}>
                  <td style={DETAIL_CELL_STYLE}>{signal.label}</td>
                  <td
                    style={{
                      ...DETAIL_CELL_STYLE,
                      fontFamily: "var(--gt-sys-font-family-mono)",
                    }}
                  >
                    {signal.valueText}
                  </td>
                  <td style={DETAIL_CELL_STYLE}>
                    <span style={{ display: "inline-flex", alignItems: "center" }}>
                      <Dot tone={toneOfVerdict(signal.verdict)} />
                      {signal.verdictText}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      />
    </Panel>
  );
}

/* A-GTCC-ROWGRID1: Schrift und Zellenpolsterung kommen aus den kanonischen
   `ControlPrimitives` (Ursache 2 des Abgleichs A-GTCC-ACCEPT-ALL2). Diese
   Detailtabelle sitzt buendig in der Kachel und behaelt darum als einziges
   lokales Mass `paddingInline: 0`; alles Vertikale — das, was das Zeilenraster
   bestimmt — ist kanonisch. */
const DETAIL_HEAD_STYLE: CSSProperties = {
  ...TH_STYLE,
  paddingInline: 0,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  borderBlockEnd: "1px solid var(--gt-sys-border)",
};

const DETAIL_CELL_STYLE: CSSProperties = {
  ...TD_STYLE,
  paddingInline: 0,
  textAlign: "start",
  borderBlockEnd: "1px solid var(--gt-sys-border)",
};

/* -------------------------------------------------------------- storage */

function storageLabel(
  row: FleetStorageRowDto,
  t: (k: string, v?: Record<string, string | number>) => string,
): string {
  return row.kind === "remote"
    ? t("admin.gtcc.cockpit.infra.storage.label_remote", { label: row.label })
    : t("admin.gtcc.cockpit.infra.storage.label_local", { host: row.host, label: row.label });
}

function StorageValue(props: { row: FleetStorageRowDto }): ReactNode {
  const { t, locale } = useLocale();
  const { row } = props;
  if (!row.measurable || row.usedPercent === null) return <NotMeasurable />;
  const percent = formatWholePercent(row.usedPercent, locale);
  if (row.kind === "remote") return <>{row.note === "" ? percent : row.note}</>;
  if (row.freeBytes !== null) {
    return (
      <>
        {t("admin.gtcc.cockpit.infra.storage.value_free", {
          percent,
          bytes: formatGib(row.freeBytes, locale),
        })}
      </>
    );
  }
  if (row.note !== "") {
    return <>{t("admin.gtcc.cockpit.infra.storage.value_note", { percent, note: row.note })}</>;
  }
  return <>{percent}</>;
}

function StorageRows(props: { rows: ReadonlyArray<FleetStorageRowDto> }): ReactNode {
  const { t } = useLocale();
  return (
    <Stack gap={0}>
      {props.rows.map((row) => (
        <InfraRow
          key={`${row.host}-${row.label}`}
          testId={`gtcc-cockpit-infra-storage-row-${row.kind}`}
          tone={row.kind === "remote" ? "info" : row.measurable ? "ok" : "muted"}
          label={storageLabel(row, t)}
          value={<StorageValue row={row} />}
        />
      ))}
      <FleetNote testId="gtcc-cockpit-infra-storage-note">
        {t("admin.gtcc.cockpit.infra.storage.remote_note")}
      </FleetNote>
    </Stack>
  );
}

function StoragePanel(props: {
  query: UseQueryResult<FleetResult<FleetStorageDto>>;
  excerpt: boolean;
}): ReactNode {
  const { t } = useLocale();
  const data = props.query.data !== undefined && props.query.data.ok ? props.query.data.data : null;
  return (
    <Panel
      testId="gtcc-cockpit-infra-storage"
      title={
        props.excerpt
          ? t("admin.gtcc.cockpit.infra.storage.title_excerpt")
          : t("admin.gtcc.cockpit.infra.storage.title")
      }
      fresh={
        data === null ? null : (
          <InfraFreshBadge
            seconds={data.freshnessSeconds}
            measuredAt={data.measuredAt}
            stale={data.status === "stale"}
            testId="gtcc-cockpit-infra-storage-fresh"
          />
        )
      }
    >
      <FleetGate
        query={props.query}
        testId="gtcc-cockpit-infra-storage"
        render={(storage) => <StorageRows rows={storage.rows} />}
      />
    </Panel>
  );
}

/* --------------------------------------------------------- fleet summary */

function FleetSummaryPanel(props: {
  query: UseQueryResult<FleetResult<FleetSummaryDto>>;
}): ReactNode {
  const { t, locale } = useLocale();
  return (
    <Panel testId="gtcc-cockpit-infra-fleet" title={t("admin.gtcc.cockpit.infra.fleet.title")}>
      <FleetGate
        query={props.query}
        testId="gtcc-cockpit-infra-fleet"
        render={(summary) => (
          <Stack gap={0}>
            <InfraRow
              testId="gtcc-cockpit-infra-fleet-healthy"
              tone="ok"
              label={t("admin.gtcc.cockpit.infra.fleet.hosts_healthy")}
              value={t("admin.gtcc.cockpit.infra.fleet.hosts_healthy_value", {
                healthy: formatInteger(summary.hostsHealthy, locale),
                total: formatInteger(summary.hostsTotal, locale),
              })}
            />
            <InfraRow
              testId="gtcc-cockpit-infra-fleet-warnings"
              tone={summary.warnings.length > 0 ? "warn" : "ok"}
              label={t("admin.gtcc.cockpit.infra.fleet.warnings")}
              value={
                summary.warnings.length === 0
                  ? t("admin.gtcc.ovw.tile.infra.no_warnings")
                  : t("admin.gtcc.cockpit.infra.fleet.warnings_value", {
                      count: formatInteger(summary.warnings.length, locale),
                      names: summary.warnings.join(", "),
                    })
              }
            />
            <InfraRow
              testId="gtcc-cockpit-infra-fleet-drift"
              tone={summary.driftHosts > 0 ? "warn" : "ok"}
              label={t("admin.gtcc.cockpit.infra.fleet.drift")}
              value={t("admin.gtcc.cockpit.infra.fleet.drift_value", {
                count: formatInteger(summary.driftHosts, locale),
              })}
            />
            <InfraRow
              testId="gtcc-cockpit-infra-fleet-ntp"
              tone={summary.ntpMaxSkewMs === null ? "muted" : "ok"}
              label={t("admin.gtcc.cockpit.infra.fleet.ntp")}
              value={
                summary.ntpMaxSkewMs === null ? (
                  <NotMeasurable />
                ) : (
                  t("admin.gtcc.cockpit.infra.fleet.ntp_value", {
                    ms: formatInteger(summary.ntpMaxSkewMs, locale),
                  })
                )
              }
            />
          </Stack>
        )}
      />
    </Panel>
  );
}

/* ------------------------------------------------------------------ TLS */

function TlsPanel(props: { query: UseQueryResult<FleetResult<FleetTlsDto>> }): ReactNode {
  const { t, locale } = useLocale();
  return (
    <Panel testId="gtcc-cockpit-infra-tls" title={t("admin.gtcc.cockpit.infra.tls.title")}>
      <FleetGate
        query={props.query}
        testId="gtcc-cockpit-infra-tls"
        render={(tls) => (
          <Stack gap={0}>
            {tls.certs.map((cert) => (
              <InfraRow
                key={cert.domain}
                testId="gtcc-cockpit-infra-tls-row"
                tone={toneOfStatus(cert.status)}
                label={cert.domain}
                value={
                  cert.daysRemaining === null ? (
                    <NotMeasurable />
                  ) : (
                    t("admin.gtcc.cockpit.infra.tls.days", {
                      days: formatInteger(cert.daysRemaining, locale),
                    })
                  )
                }
              />
            ))}
          </Stack>
        )}
      />
    </Panel>
  );
}

/* ---------------------------------------------- sub-tabs without a source */

function UnmeasuredPanel(props: { subtab: InfraSubTabId }): ReactNode {
  const { t } = useLocale();
  return (
    <Panel
      testId={`gtcc-cockpit-infra-${props.subtab}`}
      title={t(`admin.gtcc.cockpit.subtab.infra_${props.subtab}`)}
    >
      <Stack gap={2}>
        <NotMeasurable testId={`gtcc-cockpit-infra-${props.subtab}-unmeasurable`} />
        <FleetNote>{t("admin.gtcc.cockpit.infra.no_source")}</FleetNote>
      </Stack>
    </Panel>
  );
}

/* ------------------------------------------------------------------ tab */

export interface InfraTabProps {
  readonly direction?: "ltr" | "rtl";
}

export function InfraTab(props: InfraTabProps): ReactNode {
  const { t } = useLocale();
  const [subtab, setSubtab] = useState<InfraSubTabId>(initialSubTab);
  const [requestedHost, setRequestedHost] = useState<string | null>(() =>
    readQueryParam(HOST_PARAM),
  );

  const hostsQuery = useQuery({
    queryKey: FLEET_HOSTS_KEY,
    queryFn: ({ signal }) => fetchFleetHosts(signal),
    ...FLEET_QUERY_OPTIONS,
  });
  const storageQuery = useQuery({
    queryKey: FLEET_STORAGE_KEY,
    queryFn: ({ signal }) => fetchFleetStorage(signal),
    ...FLEET_QUERY_OPTIONS,
  });
  const tlsQuery = useQuery({
    queryKey: FLEET_TLS_KEY,
    queryFn: ({ signal }) => fetchFleetTls(signal),
    ...FLEET_QUERY_OPTIONS,
  });
  const summaryQuery = useQuery({
    queryKey: FLEET_SUMMARY_KEY,
    queryFn: ({ signal }) => fetchFleetSummary(signal),
    ...FLEET_QUERY_OPTIONS,
  });

  const hostsData: FleetHostsDto | null =
    hostsQuery.data !== undefined && hostsQuery.data.ok ? hostsQuery.data.data : null;
  const hosts = hostsData?.hosts ?? [];

  /**
   * The chip selection is only valid while the host is in the payload; a host
   * that disappeared falls back to the first one instead of querying a detail
   * route for a name the fleet no longer knows.
   */
  const selectedHost =
    requestedHost !== null && hosts.some((h) => h.host === requestedHost)
      ? requestedHost
      : (hosts[0]?.host ?? null);

  const detailQuery = useQuery({
    /*
     * The literal form of `FLEET_QUERY_KEYS.hostDetail`: the static key reader
     * (A-GTCC-QUERY-KEY-CONSISTENCY1) follows a literal segment by segment and
     * reads the host as the wildcard it is. `InfraTab.test.tsx` pins the
     * literal against the helper, so the two cannot drift apart.
     */
    queryKey: ["gtcc", "cockpit", "fleet-host", selectedHost ?? ""],
    queryFn: ({ signal }) => fetchFleetHostDetail(selectedHost ?? "", signal),
    enabled: selectedHost !== null,
    ...FLEET_QUERY_OPTIONS,
  });

  const onSubtabChange = useCallback((next: string) => {
    if (!isInfraSubTabId(next)) return;
    setSubtab(next);
    writeQueryParam(SUBTAB_PARAM, next);
  }, []);

  const onHostChange = useCallback((next: string) => {
    setRequestedHost(next);
    writeQueryParam(HOST_PARAM, next);
  }, []);

  const items: ReadonlyArray<TabItem> = INFRA_SUBTAB_IDS.map((id) => ({
    id,
    label: t(`admin.gtcc.cockpit.subtab.infra_${id}`),
    testId: `gtcc-cockpit-infra-subtab-${id}`,
  }));

  return (
    <Stack gap={3}>
      <div style={SUBTAB_BAR_STYLE}>
        <Tabs
          items={items}
          value={subtab}
          onChange={onSubtabChange}
          ariaLabel={t("admin.gtcc.cockpit.subtabs_infra_aria")}
          idPrefix="gtcc-cockpit-infra"
          direction={props.direction ?? "ltr"}
          variant="underline"
          dataTestId="gtcc-cockpit-infra-subtablist"
        />
      </div>

      <Inline gap={2} wrap>
        {hosts.map((host) => {
          const active = host.host === selectedHost;
          return (
            <button
              key={host.host}
              type="button"
              aria-pressed={active}
              data-testid={`gtcc-cockpit-infra-chip-${host.host}`}
              style={active ? CHIP_ACTIVE_STYLE : CHIP_STYLE}
              onClick={() => onHostChange(host.host)}
            >
              {host.host}
            </button>
          );
        })}
      </Inline>

      <div
        role="tabpanel"
        id={`gtcc-cockpit-infra-panel-${subtab}`}
        aria-labelledby={`gtcc-cockpit-infra-tab-${subtab}`}
        data-testid={`gtcc-cockpit-infra-panel-${subtab}`}
      >
        {subtab === "overview" ? (
          <div style={GRID_STYLE}>
            <Stack gap={3}>
              {selectedHost === null ? (
                <Panel
                  testId="gtcc-cockpit-infra-host-detail"
                  title={t("admin.gtcc.cockpit.infra.detail.no_host_title")}
                >
                  <NotMeasurable />
                </Panel>
              ) : (
                <HostDetailPanel host={selectedHost} query={detailQuery} />
              )}
              <StoragePanel query={storageQuery} excerpt />
            </Stack>
            <Stack gap={3}>
              <FleetSummaryPanel query={summaryQuery} />
              <TlsPanel query={tlsQuery} />
            </Stack>
          </div>
        ) : null}

        {subtab === "hosts" ? (
          <Panel
            testId="gtcc-cockpit-infra-hosts"
            title={t("admin.gtcc.cockpit.infra.hosts.title")}
            fresh={
              hostsData === null ? null : (
                <InfraFreshBadge
                  seconds={hostsData.freshnessSeconds}
                  measuredAt={hostsData.measuredAt}
                  stale={hostsData.status === "stale"}
                  testId="gtcc-cockpit-infra-hosts-fresh"
                />
              )
            }
          >
            <FleetGate
              query={hostsQuery}
              testId="gtcc-cockpit-infra-hosts"
              render={(data) => <HostTable hosts={data.hosts} />}
            />
          </Panel>
        ) : null}

        {subtab === "storage" ? <StoragePanel query={storageQuery} excerpt={false} /> : null}

        {subtab === "network" || subtab === "backups" || subtab === "hardening" ? (
          <UnmeasuredPanel subtab={subtab} />
        ) : null}
      </div>
    </Stack>
  );
}
